import { useEffect, useState } from 'react';
import FidesChatAdminService, {
  ChatMessage,
  ChatMessageContext,
} from '../../services/admin/FidesChatAdminService';
import type { ChatParticipantAddedEvent } from '../../utils/chatParticipantAddedNote';

export interface ChatTranscriptData {
  conversationId: string;
  contactExternalId: string;
  contactName: string | null;
  status: 'open' | 'resolved';
  context: ChatMessageContext | null;
  resolvedBy: 'admin' | 'investor' | null;
  messages: ChatMessage[];
  events: ChatParticipantAddedEvent[];
}

export default function useFidesChatTranscript(conversationId: string | null, enabled = true) {
  const [data, setData] = useState<ChatTranscriptData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !conversationId) {
      setData(null);
      setLoading(false);
      setError(null);
      return undefined;
    }

    let isMounted = true;
    setLoading(true);
    setError(null);

    void FidesChatAdminService.getTranscript(conversationId)
      .then((transcript) => {
        if (!isMounted) return;
        setData(transcript);
      })
      .catch((transcriptError) => {
        console.error('Failed to load chat transcript', transcriptError);
        if (!isMounted) return;
        setError('Unable to load chat transcript.');
        setData(null);
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [conversationId, enabled]);

  return {
    data,
    messages: data?.messages ?? [],
    events: data?.events ?? [],
    loading,
    error,
  };
}
